import {DataGrid, DataGridProps} from '@mui/x-data-grid';
import {neutral, brand} from 'src/theme';
import EmptyState from './EmptyState';

/**
 * Standard DataGrid wrapper with Entrio design tokens applied.
 *
 * Replaces: all per-page StyledDataGrid definitions (Tenant, Delivery,
 * AmenityReservationsPanel) and all inline DataGrid sx blocks (CallEvent,
 * DoorEvent, Users, Devices, AccessGroup, etc.)
 *
 * Usage:
 *   <EntioDataGrid
 *     rows={data}
 *     columns={columns}
 *     loading={loading}
 *     emptyMessage="No residents found"
 *   />
 */

interface EntioDataGridProps extends DataGridProps {
  /** Text to display when there are no rows */
  emptyMessage?: string;
  emptyDescription?: string;
  /** Taller rows for content with thumbnails or avatars */
  richRows?: boolean;
}

const gridSx = {
  border: 'none',
  '& .MuiDataGrid-columnHeaders': {
    bgcolor: neutral[25],
    borderBottom: `1px solid ${neutral[200]}`,
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    fontSize: '0.75rem',
    color: neutral[500],
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${neutral[100]}`,
    fontSize: '0.875rem',
    color: neutral[700],
  },
  '& .MuiDataGrid-row': {
    cursor: 'pointer',
    transition: 'background-color 0.12s ease',
    '&:hover': {bgcolor: neutral[50]},
    '&.Mui-selected': {
      bgcolor: brand[50],
      '&:hover': {bgcolor: brand[50]},
    },
  },
  '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {outline: 'none'},
  '& .MuiDataGrid-row:focus, & .MuiDataGrid-row:focus-within': {outline: 'none'},
  '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {outline: 'none'},
  '& .MuiDataGrid-footerContainer': {
    borderTop: `1px solid ${neutral[100]}`,
  },
  '& .MuiDataGrid-overlay': {bgcolor: 'transparent', minHeight: 200},
  '& .MuiDataGrid-overlayWrapper': {minHeight: 200},
  '& .MuiDataGrid-sortIcon': {
    opacity: 0,
    transition: 'opacity 0.2s',
  },
  '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-sortIcon, & .MuiDataGrid-columnHeaderActive .MuiDataGrid-sortIcon': {
    opacity: 0.7,
  },
};

export default function EntioDataGrid({
  emptyMessage = 'No data',
  emptyDescription,
  richRows,
  sx,
  ...props
}: EntioDataGridProps) {
  return (
    <DataGrid
      disableColumnFilter
      disableColumnMenu
      disableColumnSelector
      disableRowSelectionOnClick
      autoHeight
      rowHeight={richRows ? 64 : 48}
      pageSizeOptions={[10, 25, 50]}
      initialState={{
        pagination: {paginationModel: {page: 0, pageSize: 10}},
      }}
      slots={{
        noRowsOverlay: () => (
          <EmptyState compact title={emptyMessage} description={emptyDescription} />
        ),
        ...props.slots,
      }}
      sx={{
        ...gridSx,
        ...sx,
      }}
      {...props}
    />
  );
}
